// src/hooks/useSubscription.ts

import { useState, useEffect, useCallback } from 'react';
import { SubscriptionInfo } from '../types/user';
import { User } from '../services/auth';
import { getTestScenarioFromURL, getTestScenarioFromStorage } from '../utils/devTestHelpers';
import { secureCall } from '../services/secureApiClient';
import { logWarn } from '../utils/logger';

export interface SubscriptionDisplay {
  planDisplay: string;
  badge?: { text: string; className: string };
  rightText: string;
  showUpgrade: boolean;
  billingCycle?: string;
}

export interface SubscriptionStatus {
  // 基本狀態
  isLoading: boolean;
  error: string | null;
  
  // 顯示信息（由後端計算）
  display?: SubscriptionDisplay;
  
  // 原始訂閱信息（保留兼容性）
  subscription?: SubscriptionInfo;
  
  // AI 使用權限
  canUseAI: boolean;
  
  // 方法
  refreshStatus: () => Promise<void>;
}

// 快取機制
interface CacheEntry {
  data: SubscriptionDisplay;
  subscription: SubscriptionInfo;
  timestamp: number;
  userId: string;
}

const subscriptionCache = new Map<string, CacheEntry>();
const CACHE_DURATION = 30 * 1000; // 30秒快取

export const useSubscription = (user: User | null): SubscriptionStatus => {
  // ======================================================
  // 開發測試模式檢查（但不提前返回）
  // ======================================================

  const mockState = getTestScenarioFromURL() || getTestScenarioFromStorage();
  const isMockMode = !!mockState;

  // ======================================================
  // 狀態管理（無論是否測試模式都調用）
  // ======================================================

  const [isLoading, setIsLoading] = useState(!isMockMode);
  const [error, setError] = useState<string | null>(null);
  const [display, setDisplay] = useState<SubscriptionDisplay | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  
  // ======================================================
  // AI 使用權限判斷 - 簡化版本，基於後端返回的顯示狀態
  // ======================================================
  
  const canUseAI = (() => {
    if (isMockMode && mockState) {
      // 測試模式邏輯保持不變
      if (mockState.status === 'trialing') return true;
      if (mockState.status === 'active') return true;
      return true; // 簡化測試，全部允許
    }
    
    // 生產模式：基於顯示狀態判斷
    if (!display) return false;
    
    // 如果顯示為 Pro（無論是 Active、Trial 或其他），允許使用 AI
    if (display.planDisplay === 'Pro') return true;
    
    // 免費版：暫時開放無限使用
    // TODO: 實作使用量追蹤
    if (display.planDisplay === 'Free') return true;
    
    return false;
  })();
  

  // ======================================================
  // 載入訂閱狀態（測試模式下跳過）
  // ======================================================

  // ======================================================
  // 載入訂閱狀態（使用 SecureApiClient 透明處理認證）
  // ======================================================

  const loadSubscriptionStatus = useCallback(async (bypassCache = false) => {
    if (isMockMode || !user) {
      return;
    }

    const userId = user.uid;
    const cacheKey = `subscription_${userId}`;

    // 檢查快取
    if (!bypassCache) {
      const cached = subscriptionCache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
        setDisplay(cached.data);
        setSubscription(cached.subscription);
        setIsLoading(false);
        return;
      }
    }

    try {
      setIsLoading(true);
      setError(null);

      // 🎯 使用 SecureApiClient 透明處理所有認證問題
      const response = await secureCall<{ success: boolean; display: SubscriptionDisplay; error?: string }>(
        'getSubscriptionDisplayInfo'
      );

      if (response.success && response.display) {
        setDisplay(response.display);

        // 保留兼容性：創建簡化的 subscription 對象
        const compatSubscription: SubscriptionInfo = {
          plan: response.display.planDisplay === 'Pro' ? 'pro' : 'free',
          status: response.display.rightText === 'Active' ? 'active' :
            response.display.badge?.text === 'TRIAL' ? 'trialing' : 'none',
          billingCycle: (response.display.billingCycle as 'monthly' | 'yearly' | undefined) || undefined
        };

        setSubscription(compatSubscription);

        // 儲存到快取
        subscriptionCache.set(cacheKey, {
          data: response.display,
          subscription: compatSubscription,
          timestamp: Date.now(),
          userId
        });
      } else {
        logWarn('useSubscription', '後端返回錯誤', { error: response.error });
        setError(response.error || 'Failed to load subscription status');

        // 錯誤時設定預設狀態
        setDisplay({
          planDisplay: 'Free',
          rightText: 'Upgrade',
          showUpgrade: true,
          badge: undefined
        });
        setSubscription({ plan: 'free', status: 'none' });
      }
    } catch (err) {
      logWarn('useSubscription', 'API 調用失敗', {
        error: err instanceof Error ? err.message : 'Unknown error'
      });

      // 設置錯誤訊息（認證問題由 SecureApiClient 透明處理）
      setError('Failed to load subscription status');

      // 錯誤時設定預設狀態
      setDisplay({
        planDisplay: 'Free',
        rightText: 'Upgrade',
        showUpgrade: true,
        badge: undefined
      });
      setSubscription({ plan: 'free', status: 'none' });
    } finally {
      setIsLoading(false);
    }
  }, [user, isMockMode]);
  
  useEffect(() => {
    // 測試模式下不執行邏輯
    if (isMockMode) {
      return;
    }
    
    if (!user) {
      // 用戶未登入，重置狀態
      setIsLoading(false);
      setDisplay(null);
      setSubscription(null);
      setError(null);
      return;
    }
    
    // 載入訂閱狀態
    loadSubscriptionStatus();
  }, [user, isMockMode, loadSubscriptionStatus]);
  
  // ======================================================
  // 手動刷新功能
  // ======================================================
  
  const refreshStatus = useCallback(async (): Promise<void> => {
    if (isMockMode) {
      return;
    }

    if (!user) return;

    try {
      // 刷新時繞過快取
      await loadSubscriptionStatus(true);
    } catch (err) {
      logWarn('useSubscription', '刷新訂閱狀態失敗', {
        error: err instanceof Error ? err.message : 'Unknown error'
      });

      setError('Failed to refresh subscription status');
    }
  }, [user, isMockMode, loadSubscriptionStatus]);
  
  // ======================================================
  // 返回狀態（根據模式計算不同結果）
  // ======================================================
  
  // 如果是測試模式，使用模擬數據
  if (isMockMode && mockState) {
    const mockCanUseAI = (() => {
      if (mockState.status === 'trialing') return true;
      if (mockState.status === 'active') return true;
      return true; // 簡化測試，全部允許
    })();
    
    // 模擬顯示數據
    const mockDisplay: SubscriptionDisplay = {
      planDisplay: mockState.status === 'trialing' ? 'Pro' : 'Free',
      rightText: mockState.status === 'trialing' ? `${mockState.trialDaysRemaining} days left` : 'Upgrade',
      showUpgrade: mockState.status !== 'trialing' && mockState.status !== 'active',
      badge: mockState.status === 'trialing' ? 
        { text: 'TRIAL', className: 'bg-sky-100 text-sky-600 text-xs px-2 py-1 rounded-full' } : undefined
    };
    
    // 模擬 subscription 對象（兼容性）
    const mockSubscription: SubscriptionInfo = {
      plan: mockState.status === 'trialing' ? 'pro' : 'free',
      status: mockState.status as SubscriptionInfo['status'],
      trialEndsAt: mockState.trialDaysRemaining ? 
        new Date(Date.now() + mockState.trialDaysRemaining * 24 * 60 * 60 * 1000) : undefined
    };
    
    return {
      isLoading: false,
      error: null,
      display: mockDisplay,
      subscription: mockSubscription,
      canUseAI: mockCanUseAI,
      refreshStatus
    };
  }
  
  // 正常模式返回真實狀態
  return {
    isLoading,
    error,
    display: display || undefined,
    subscription: subscription || undefined,
    canUseAI,
    refreshStatus
  };
};

